import type { ReactNode } from 'react';
import {
  type Groups,
  useAuthorization
} from '@/app/lib/auth/use-authorization.ts';

interface AuthorizationProps {
  forbiddenFallback?: ReactNode;
  children: ReactNode;
  allowedGroups?: Groups;
}

/**
 * Component that renders its children only if the user is authorized to view them.
 * @param allowedGroups Groups that are allowed to view the children.
 * @param forbiddenFallback Fallback component to render if the user is not authorized.
 * @param children Children to render if the user is authorized.
 * @returns {ReactNode} Returns a React element which renders its children only if the user is authorized to view them.
 */
export function Authorization({
  allowedGroups,
  forbiddenFallback = null,
  children
}: Readonly<AuthorizationProps>): ReactNode {
  const { checkAccess } = useAuthorization();

  let canAccess = false;

  if (allowedGroups) {
    canAccess = checkAccess({ allowedGroups });
  }

  return <>{canAccess ? children : forbiddenFallback}</>;
}
